import { Component, Element, Fragment, h, Prop } from "@stencil/core"
import {
  getAppearance,
  translate,
} from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-price",
  styleUrl: "price.scss",
  shadow: true,
})
export class Price {
  @Element() public el!: HTMLSdxPriceElement

  /**
   * The amount to be paid.
   */
  @Prop() public amount: number = 0

  /**
   * Time period, for example "/mo.".
   */
  @Prop() public period: string = ""

  /**
   * The font size. Use `auto` to inherit font properties from parent.
   */
  @Prop() public size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | "auto" = 2

  /**
   * Displays a strikethrough over the amount.
   */
  @Prop() public strikethrough?: "pink" | "navy"

  /**
   * Description text read by the screen reader.
   * Leave empty for auto-generated text, i.e. "5 francs 50 per month".
   */
  @Prop() public srHint: string = ""

  /**
   * Formats a price like 5.–, 5.50, –.50, or 0.–
   */
  private getFormattedAmount() {
    const amount = Math.abs(this.amount) || 0 // -10 -> 10 and NaN -> 0
    return `${Math.round(amount * 100)}` // 10.1 -> 1010
      .replace(/^0$/, "000") // 0 -> 000
      .replace(/^(.)$/, "0$1") // 5 -> 05
      .replace(/(..)$/, ".$1") // 1010 -> 10.10
      .replace(/00$|^(?=[.])/, "–") // 10.00 -> 10.– and .10 -> –.10
  }

  private isInteger() {
    return this.amount === Math.floor(this.amount)
  }

  private getSrHint() {
    if (this.srHint) {
      return this.srHint
    }

    const srHint = [],
      [francs, centimes] = this.getFormattedAmount().split("."),
      hasFrancs = Number(francs) > 0,
      hasCentimes = Number(centimes) > 0,
      i18n = (text: string) => translate(text, this.el.lang),
      period = this.period
        .replace("/", "")
        .replace(/(Monat|Mt[.]|mois|mese|mth[.]?|mo[.])/i, i18n("Month")),
      lang = this.el.lang || document.documentElement.lang || "en"

    if (hasFrancs || !hasCentimes) {
      srHint.push(francs, i18n("francs"))
    }

    if (hasCentimes) {
      if (/fr|it/i.test(lang)) {
        srHint.push(i18n("and"))
      }

      srHint.push(centimes)

      if (!hasFrancs) {
        srHint.push(i18n("centimes"))
      }
    }

    if (period) {
      srHint.push(i18n("per"), period)
    }

    return srHint.join(" ")
  }

  private getFontClass() {
    if (this.size === "auto") {
      return "auto"
    }

    return `text-${this.size > 6 ? "d" : "h"}${
      (this.size > 6 ? 9 : 6) - this.size + 1
    }`
  }

  private getComponentClassNames() {
    return {
      component: true,
      integer: this.isInteger(),
      [this.getFontClass()]: true,
      [getAppearance(this.el)]: true,
    }
  }

  private getStrikethroughClass() {
    return {
      ["strikethrough"]: true,
      [`strikethrough--${this.strikethrough}`]: true,
    }
  }

  public render() {
    return (
      <Fragment>
        <span class={this.getComponentClassNames()} aria-hidden="true">
          <span class={this.strikethrough && this.getStrikethroughClass()}>
            {this.getFormattedAmount()}
          </span>

          <span class="period">{this.period}</span>
        </span>
        <span class="sr-only">{this.getSrHint()}</span>
      </Fragment>
    )
  }
}
